<template>
  <DSDateTimeFormField
    ref="fieldRef"
    v-model:input="formattedModelValue"
    v-model:open="popoverOpen"
    :name="props.name"
    :label="props.label"
    :inline-edit="props.inlineEdit"
    :tooltip="props.tooltip"
    :hidden-label="props.hiddenLabel"
    :disabled="props.disabled"
    :placeholder="placeholder"
    action-icon="calendar"
    :error-messages="props.errorMessages"
    :clearable="props.clearable"
    :rules="props.rules"
    @blur="handleBlur"
    @focus="emit('focus', $event)"
    @close="handleClose"
  >
    <DSDatepickerCalendar
      v-model="pickerModelValue"
      :disabled="props.disabled"
      :disabled-dates="props.disabledDates"
    />
  </DSDateTimeFormField>
  <input
    type="hidden"
    :name="props.name"
    :value="hiddenInputValue"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { DSDatepickerEmits } from "../types/emits";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";
import DSDatepickerCalendar from "@/src/design_system/form_elements/datepicker/components/DSDatepickerCalendar.vue";
import { easySettings } from "@/src/shared/store/easyGlobalSettings";
import type { DSDatePickerProps } from "@/src/design_system/form_elements/datepicker/types/datepicker";
import {
  DS_DATE_TIME_PICKER_MODE,
  DSDateTimePickerModelValue,
} from "@/src/design_system/form_elements/shared/types/dateTimePicker";
import DSDateTimeFormField from "@/src/design_system/form_elements/shared_modules/date_time_form_field/components/DSDateTimeFormField.vue";
import { useDateTimePickers } from "@/src/design_system/form_elements/shared/composables/useDateTimePickers";
import { DEFAULT_DATE_TIME_PLACEHOLDER } from "@/src/design_system/form_elements/shared/constants/dateTimePlaceholders";
import { BE_DATE_FORMAT } from "@/src/shared/constants/date";
import dayjs from "dayjs";

const props = withDefaults(defineProps<DSDatePickerProps>(), {
  hiddenLabel: false,
  inlineEdit: false,
  disabled: false,
  tooltip: undefined,
  disabledDates: undefined,
  clearable: true,
  rules: undefined,
});

const modelValue = defineModel<DSDateTimePickerModelValue>({ default: null });
const emit = defineEmits<DSDatepickerEmits>();
const fieldRef = ref<InstanceType<typeof DSDateTimeFormField>>();
const { focus, blur } = useDSInput(fieldRef);

const defaultPlaceholder = computed(() =>
  dayjs(DEFAULT_DATE_TIME_PLACEHOLDER).format(easySettings.getGlobalDateFormat.value)
);

const placeholder = computed(() => props.placeholder ?? defaultPlaceholder.value);

const { formattedModelValue, pickerModelValue, temporalFormattedModelValue, popoverOpen, hiddenInputValue } =
  useDateTimePickers(DS_DATE_TIME_PICKER_MODE.Date, modelValue, {
    input: easySettings.getGlobalDateFormat.value,
    hiddenInput: BE_DATE_FORMAT,
  });

const handleBlur = (event: FocusEvent) => {
  temporalFormattedModelValue.value = null;
  emit("blur", event);
};

const handleClose = () => {
  temporalFormattedModelValue.value = null;
  emit("close");
};

defineExpose({ focus, blur });
</script>
